/**
 * 시딩 비용 계산 — 계정 예산 합 · 비용 기준 · 단가(CPV·CPE). 화면 표·그룹 줄·MCP 시딩 요약이 같은 함수를 쓴다.
 * 표시 문자열은 `sections/campaign/utils/campaign-list-format` 의 `formatCostPerUnitWon` 이 이 숫자로 만든다.
 */

import { sumCampaignPostBudgets, sumForcedAdditionBudgets } from './campaign-sources';

/**
 * 인플루언서(계정)별로 등록된 예산을 합산한다. 문자열 형식 계정은 예산 0으로 취급한다.
 * @param accounts 캠페인 조건의 계정 목록(객체·문자열 섞임)
 * @returns 원 단위 합
 */
export function sumAccountsBudget(accounts: unknown): number {
  if (!Array.isArray(accounts) || accounts.length === 0) return 0;
  return accounts.reduce((sum: number, acc: any) => {
    if (acc && typeof acc === 'object') return sum + (Number(acc?.budget) || 0);
    return sum;
  }, 0);
}

/**
 * 캠페인 **총 예산** — 등록 계정 예산 합 + 링크 예산 합(`forcedAdditionBudgets` · 아직 안 모인 링크 몫 포함)
 * + 글 예산 합(`postBudgets` — 같은 글에 링크 예산이 있으면 빼고 센다 · `readCampaignPostBudgets`).
 * 🔑 캠페인 목록(줄·그룹 줄·합계)·상세·홈 시딩 모듈이 이 하나로 센다 — 따로 더하면 화면마다 비용이 갈린다.
 * @param campaign 캠페인 문서(링크 예산을 읽는다)
 * @param registeredAccounts 등록한 시딩 계정(`buildCampaignCriteria(...).accounts` · `getCampaignAccounts`)
 */
export function campaignTotalBudgetWon(campaign: unknown, registeredAccounts: unknown): number {
  return sumAccountsBudget(registeredAccounts) + sumForcedAdditionBudgets(campaign) + sumCampaignPostBudgets(campaign);
}

/**
 * CPV·CPE 의 분자 — 집행 예산이 있으면 그것, 없으면 계정 예산 합.
 * @param spentBudgetWon 캠페인 문서의 `spentBudget`
 * @param accountsBudgetWon `sumAccountsBudget` 결과
 */
export function campaignCostBasisWon(spentBudgetWon: unknown, accountsBudgetWon: unknown): number {
  const spent = Number(spentBudgetWon) || 0;
  return spent > 0 ? spent : (Number(accountsBudgetWon) || 0);
}

/**
 * 단가(원) = 비용 ÷ 건수(조회 또는 반응). 100원 이상은 정수로, 미만은 소수 둘째 자리로 반올림한다.
 * @returns 비용·건수가 0 이하이거나 유한하지 않으면 `null`
 */
export function costPerUnitWon(costWon: number, count: number): number | null {
  const cost = Number(costWon) || 0;
  const n = Number(count) || 0;
  if (cost <= 0 || n <= 0) return null;
  const v = cost / n;
  if (!Number.isFinite(v)) return null;
  return v >= 100 ? Math.round(v) : Number(v.toFixed(2));
}
